
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow bg-white">
        {/* Hero Section */}
        <section className="bg-stone-50 py-8">
          <div className="container mx-auto px-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Privacy Policy</h1>
            <p className="text-sm text-gray-600">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-8">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="prose prose-lg max-w-none">
              
              {/* Introduction */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">1. Introduction</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  Vine Consulting Engineers ("we", "our", or "us") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                </p>
                <p className="text-gray-600 leading-relaxed text-xs">
                  This policy complies with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                </p>
              </div>

              {/* Data Controller */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">2. Data Controller</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  Vine Consulting Engineers is the data controller responsible for your personal data.
                </p>
                <div className="bg-stone-50 p-4 rounded-lg">
                  <p className="text-gray-700 mb-1 text-xs"><strong>Company Name:</strong> Vine Consulting Engineers</p>
                  <p className="text-gray-700 mb-1 text-xs"><strong>Registered Address:</strong> Alton, Hampshire, GU34 2UW</p>
                  <p className="text-gray-700 mb-1 text-xs"><strong>Company Number:</strong> 12346799</p>
                  <p className="text-gray-700 mb-1 text-xs"><strong>Email:</strong> Mail@VineCE.co.uk</p>
                  <p className="text-gray-700 text-xs"><strong>Phone:</strong> +44(0) 7774 218 135</p>
                </div>
              </div>

              {/* Information We Collect */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">3. Information We Collect</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  We may collect and process the following categories of personal data:
                </p>
                
                <h3 className="text-sm font-semibold text-gray-800 mb-2">3.1 Information You Provide</h3>
                <ul className="list-disc pl-6 mb-3 text-gray-600 space-y-1 text-xs">
                  <li>Contact information (name, email address, phone number, company name)</li>
                  <li>Project details and requirements you share with us</li>
                  <li>Communication preferences</li>
                  <li>Any other information you choose to provide when contacting us</li>
                </ul>

                <h3 className="text-sm font-semibold text-gray-800 mb-2">3.2 Information Automatically Collected</h3>
                <ul className="list-disc pl-6 mb-3 text-gray-600 space-y-1 text-xs">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and time spent on our website</li>
                  <li>Referring website addresses</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>

              {/* How We Use Your Information */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">4. How We Use Your Information</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  We process your personal data for the following purposes and legal bases:
                </p>
                
                <div className="space-y-2">
                  <div className="bg-stone-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-1 text-xs">Contract Performance</h4>
                    <p className="text-gray-600 text-xs">To provide engineering services, respond to inquiries, and fulfill contractual obligations.</p>
                  </div>
                  
                  <div className="bg-stone-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-1 text-xs">Legitimate Interests</h4>
                    <p className="text-gray-600 text-xs">To improve our services, maintain website security, and conduct business development activities.</p>
                  </div>
                  
                  <div className="bg-stone-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-1 text-xs">Legal Obligations</h4>
                    <p className="text-gray-600 text-xs">To comply with legal and regulatory requirements, including health and safety regulations.</p>
                  </div>
                  
                  <div className="bg-stone-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-1 text-xs">Consent</h4>
                    <p className="text-gray-600 text-xs">To send marketing communications where you have provided consent (you may withdraw consent at any time).</p>
                  </div>
                </div>
              </div>

              {/* Data Sharing */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">5. Sharing Your Information</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  We may share your personal data with:
                </p>
                <ul className="list-disc pl-6 mb-2 text-gray-600 space-y-1 text-xs">
                  <li>Professional advisers (lawyers, accountants, insurers)</li>
                  <li>Contractors and subcontractors working on projects</li>
                  <li>Regulatory authorities and government bodies when required by law</li>
                  <li>IT service providers who support our business operations</li>
                </ul>
                <p className="text-gray-600 leading-relaxed text-xs">
                  We do not sell your personal data to third parties. All third parties are required to maintain appropriate security measures and use your data only as instructed.
                </p>
              </div>

              {/* Data Retention */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">6. Data Retention</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-1 text-xs">
                  <li>Client project data: 6 years after project completion (in line with professional indemnity insurance requirements)</li>
                  <li>Contact inquiries: 2 years from last contact</li>
                  <li>Marketing data: Until you withdraw consent or request deletion</li>
                  <li>Website analytics: 26 months</li>
                </ul>
              </div>

              {/* Your Rights */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">7. Your Rights Under UK GDPR</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  You have the following rights regarding your personal data:
                </p>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 mt-1 mr-3 text-xs"></i>
                    <div className="text-xs">
                      <strong className="text-gray-800">Right of Access:</strong>
                      <span className="text-gray-600"> Request copies of your personal data</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 mt-1 mr-3 text-xs"></i>
                    <div className="text-xs">
                      <strong className="text-gray-800">Right to Rectification:</strong>
                      <span className="text-gray-600"> Request correction of inaccurate data</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 mt-1 mr-3 text-xs"></i>
                    <div className="text-xs">
                      <strong className="text-gray-800">Right to Erasure:</strong>
                      <span className="text-gray-600"> Request deletion of your data in certain circumstances</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 mt-1 mr-3 text-xs"></i>
                    <div className="text-xs">
                      <strong className="text-gray-800">Right to Restrict Processing:</strong>
                      <span className="text-gray-600"> Request limitation of how we use your data</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 mt-1 mr-3 text-xs"></i>
                    <div className="text-xs">
                      <strong className="text-gray-800">Right to Data Portability:</strong>
                      <span className="text-gray-600"> Request transfer of your data to another organization</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 mt-1 mr-3 text-xs"></i>
                    <div className="text-xs">
                      <strong className="text-gray-800">Right to Object:</strong>
                      <span className="text-gray-600"> Object to processing based on legitimate interests or for marketing purposes</span>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-emerald-600 mt-1 mr-3 text-xs"></i>
                    <div className="text-xs">
                      <strong className="text-gray-800">Right to Withdraw Consent:</strong>
                      <span className="text-gray-600"> Withdraw consent where processing is based on consent</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mt-2 text-xs">
                  To exercise any of these rights, please contact us at Mail@VineCE.co.uk. We will respond within one month.
                </p>
              </div>

              {/* Cookies */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">8. Cookies</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  Our website uses cookies to improve your browsing experience. Cookies are small text files stored on your device.
                </p>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Types of Cookies We Use:</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-1 text-xs">
                  <li><strong>Essential Cookies:</strong> Required for website functionality</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                  <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mt-2 text-xs">
                  You can control cookies through your browser settings. Disabling cookies may affect website functionality.
                </p>
              </div>

              {/* Security */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">9. Data Security</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-1 text-xs">
                  <li>Secure server infrastructure and encrypted data transmission</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication procedures</li>
                  <li>Staff training on data protection and confidentiality</li>
                </ul>
              </div>

              {/* International Transfers */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">10. International Data Transfers</h2>
                <p className="text-gray-600 leading-relaxed text-xs">
                  We primarily process data within the United Kingdom. If we transfer data outside the UK, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses or adequacy decisions, to protect your information in accordance with UK GDPR requirements.
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">11. Children's Privacy</h2>
                <p className="text-gray-600 leading-relaxed text-xs">
                  Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal data from children. If you believe we have collected information from a child, please contact us immediately.
                </p>
              </div>

              {/* Changes to Policy */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">12. Changes to This Policy</h2>
                <p className="text-gray-600 leading-relaxed text-xs">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of significant changes by posting the updated policy on our website with a new "Last updated" date. We encourage you to review this policy periodically.
                </p>
              </div>

              {/* Complaints */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">13. Complaints</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  If you have concerns about how we handle your personal data, please contact us first. You also have the right to lodge a complaint with the UK supervisory authority:
                </p>
                <div className="bg-stone-50 p-4 rounded-lg">
                  <p className="text-gray-700 mb-1 text-xs"><strong>Information Commissioner's Office (ICO)</strong></p>
                  <p className="text-gray-600 mb-1 text-xs">Wycliffe House, Water Lane</p>
                  <p className="text-gray-600 mb-1 text-xs">Wilmslow, Cheshire SK9 5AF</p>
                  <p className="text-gray-600 mb-1 text-xs">Tel: 0303 123 1113</p>
                  <p className="text-gray-600 text-xs">Website: www.ico.org.uk</p>
                </div>
              </div>

              {/* Contact Us */}
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">14. Contact Us</h2>
                <p className="text-gray-600 leading-relaxed mb-2 text-xs">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <p className="text-gray-700 mb-1 text-xs"><strong>Vine Consulting Engineers</strong></p>
                  <p className="text-gray-600 mb-1 text-xs">
                    <i className="ri-mail-line text-emerald-600 mr-2"></i>
                    Email: Mail@VineCE.co.uk
                  </p>
                  <p className="text-gray-600 mb-1 text-xs">
                    <i className="ri-phone-line text-emerald-600 mr-2"></i>
                    Phone: +44(0) 7774 218 135
                  </p>
                  <p className="text-gray-600 text-xs">
                    <i className="ri-map-pin-line text-emerald-600 mr-2"></i>
                    Address: Alton, Hampshire, GU34 2UW
                  </p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}